import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import {
  newElementWith,
  selectGroupsForSelectedElements,
} from "@excalidraw/element";
import { CaptureUpdateAction } from "@excalidraw/element";

import {
  actionToggleElementHidden,
  actionRenameElement,
  getGroupName,
} from "../../actions/actionLayers";
import { actionGroup, actionUngroup } from "../../actions/actionGroup";
import {
  actionBringForward,
  actionSendBackward,
  actionBringToFront,
  actionSendToBack,
} from "../../actions/actionZindex";
import { actionDeleteSelected } from "../../actions/actionDeleteSelected";
import {
  useApp,
  useExcalidrawActionManager,
  useExcalidrawAppState,
  useExcalidrawElements,
} from "../App";
import { eyeIcon, eyeClosedIcon, LockedIcon, UnlockedIcon } from "../icons";

import {
  buildLayerTree,
  flattenVisibleNodes,
  collectElementIds,
  selectionElementIds,
} from "./layerTree";
import { computeDropResult } from "./layerDrop";

import "./LayersPanel.scss";

import type { DropTarget } from "./layerDrop";
import type { LayerNode } from "./layerTree";

// Rows render their icon at 12px. The toolbar icons for these types are
// unusable at that size: Rectangle/Ellipse/Diamond are solid-filled (they read
// as blobs, and as the same blob once tinted), while Text/Image/Freedraw are
// drawn on 40–50px viewBoxes with detail that collapses. These are outline
// glyphs on one 24×24 / 1.75-stroke grid so every row reads at the same weight.
const glyph = (children: React.ReactNode) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

/** keyed by element `type`, plus the two non-element node kinds */
const LAYER_GLYPHS: Record<string, React.ReactNode> = {
  group: glyph(
    <>
      <rect x="3" y="3" width="13" height="13" rx="2" strokeDasharray="3 2" />
      <rect x="8" y="8" width="13" height="13" rx="2" />
    </>,
  ),
  frame: glyph(<path d="M4 7.5h16M4 16.5h16M7.5 4v16M16.5 4v16" />),
  magicframe: glyph(
    <>
      <path d="M4 7.5h16M4 16.5h16M7.5 4v16M16.5 4v16" />
      <path d="M12 9.5l.9 1.6 1.6.9-1.6.9-.9 1.6-.9-1.6-1.6-.9 1.6-.9z" />
    </>,
  ),
  rectangle: glyph(<rect x="3.5" y="5" width="17" height="14" rx="2" />),
  ellipse: glyph(<ellipse cx="12" cy="12" rx="8.5" ry="7" />),
  diamond: glyph(<path d="M12 3.5 20.5 12 12 20.5 3.5 12Z" />),
  arrow: glyph(<path d="M4 19 20 5M13 5h7v7" />),
  line: glyph(<path d="M4 19 20 5" />),
  freedraw: glyph(
    <>
      <path d="M4.5 19.5 5.5 15.5 16.5 4.5a2.12 2.12 0 0 1 3 3l-11 11z" />
      <path d="M14.5 6.5l3 3" />
    </>,
  ),
  text: glyph(<path d="M5 7V5h14v2M12 5v14M9.5 19h5" />),
  image: glyph(
    <>
      <rect x="3.5" y="5" width="17" height="14" rx="2" />
      <circle cx="8.5" cy="9.75" r="1.25" />
      <path d="M4 17.5 9.5 12l3.5 3.5L16 12.5l4 4" />
    </>,
  ),
  // embeds render as a web view on canvas — a browser window, not a shape
  embeddable: glyph(
    <>
      <rect x="3.5" y="5" width="17" height="14" rx="2" />
      <path d="M3.5 9.5h17M7 7.25h.01M9.5 7.25h.01" />
    </>,
  ),
};
LAYER_GLYPHS.iframe = LAYER_GLYPHS.embeddable;

const nodeIcon = (node: LayerNode): React.ReactNode =>
  // group/frame nodes may exist before their element does (frame placeholders),
  // so fall back on the node kind, then on rectangle for unknown element types
  LAYER_GLYPHS[node.element?.type ?? ""] ??
  LAYER_GLYPHS[node.kind] ??
  LAYER_GLYPHS.rectangle;

/** stable per-row icon key — also what the icon test asserts on */
const nodeIconName = (node: LayerNode): string => {
  const type = node.element?.type ?? "";
  if (LAYER_GLYPHS[type]) {
    return type === "iframe" ? "embeddable" : type;
  }
  return LAYER_GLYPHS[node.kind] ? node.kind : "rectangle";
};

const LayerRow = ({
  node,
  depth,
  expanded,
  selected,
  hasSelectedChildren,
  renaming,
  dropIndicator,
  onToggleExpand,
  onRowClick,
  onRowDoubleClick,
  onRowContextMenu,
  onRowPointerDown,
  onRenameCommit,
  onRenameCancel,
  onToggleHidden,
  onToggleLock,
}: {
  node: LayerNode;
  depth: number;
  expanded: boolean;
  selected: boolean;
  hasSelectedChildren: boolean;
  renaming: boolean;
  dropIndicator: "above" | "below" | "inside" | null;
  onToggleExpand: (id: string) => void;
  onRowClick: (node: LayerNode, event: React.MouseEvent) => void;
  onRowDoubleClick: (node: LayerNode) => void;
  onRowContextMenu: (node: LayerNode, event: React.MouseEvent) => void;
  onRowPointerDown: (node: LayerNode, event: React.PointerEvent) => void;
  onRenameCommit: (node: LayerNode, value: string) => void;
  onRenameCancel: () => void;
  onToggleHidden: (node: LayerNode) => void;
  onToggleLock: (node: LayerNode) => void;
}) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const renameCancelledRef = useRef(false);

  useEffect(() => {
    if (selected) {
      // optional-call: jsdom doesn't implement scrollIntoView
      rowRef.current?.scrollIntoView?.({ block: "nearest" });
    }
  }, [selected]);

  return (
    <div
      ref={rowRef}
      className={`layers-panel__row${
        selected ? " layers-panel__row--selected" : ""
      }${
        !selected && hasSelectedChildren
          ? " layers-panel__row--has-selected-children"
          : ""
      }${node.hidden ? " layers-panel__row--hidden" : ""}${
        renaming ? " layers-panel__row--editing" : ""
      }${dropIndicator ? ` layers-panel__row--drop-${dropIndicator}` : ""}`}
      style={{ "--depth": depth } as React.CSSProperties}
      data-node-id={node.id}
      data-kind={node.kind}
      onClick={(event) => onRowClick(node, event)}
      onDoubleClick={() => onRowDoubleClick(node)}
      onContextMenu={(event) => onRowContextMenu(node, event)}
      onPointerDown={(event) => onRowPointerDown(node, event)}
    >
      {node.children.length > 0 ? (
        <button
          type="button"
          className={`layers-panel__chevron${
            expanded ? " layers-panel__chevron--open" : ""
          }`}
          onClick={(event) => {
            event.stopPropagation();
            onToggleExpand(node.id);
          }}
          onPointerDown={(event) => event.stopPropagation()}
          onDoubleClick={(event) => event.stopPropagation()}
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          <svg width="8" height="8" viewBox="0 0 8 8">
            <path d="M2 1l4 3-4 3z" fill="currentColor" />
          </svg>
        </button>
      ) : (
        <span className="layers-panel__chevron-spacer" />
      )}
      <span className="layers-panel__icon" data-icon={nodeIconName(node)}>
        {nodeIcon(node)}
      </span>
      {renaming ? (
        <input
          className="layers-panel__rename-input"
          defaultValue={node.named ? node.label : ""}
          placeholder={node.label}
          autoFocus
          onFocus={(event) => event.target.select()}
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          onDoubleClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              onRenameCommit(node, (event.target as HTMLInputElement).value);
            } else if (event.key === "Escape") {
              renameCancelledRef.current = true;
              onRenameCancel();
            }
            event.stopPropagation();
          }}
          onBlur={(event) => {
            if (!renameCancelledRef.current) {
              onRenameCommit(node, event.target.value);
            }
          }}
        />
      ) : (
        <span
          className={`layers-panel__label${
            node.named ? "" : " layers-panel__label--default"
          }`}
        >
          {node.label}
        </span>
      )}
      <button
        type="button"
        className="layers-panel__row-action layers-panel__lock"
        data-persistent={node.locked || undefined}
        aria-label={node.locked ? "Unlock" : "Lock"}
        onClick={(event) => {
          event.stopPropagation();
          onToggleLock(node);
        }}
        onPointerDown={(event) => event.stopPropagation()}
        onDoubleClick={(event) => event.stopPropagation()}
      >
        {node.locked ? LockedIcon : UnlockedIcon}
      </button>
      <button
        type="button"
        className="layers-panel__row-action layers-panel__eye"
        data-persistent={node.hidden || undefined}
        aria-label={node.hidden ? "Show" : "Hide"}
        onClick={(event) => {
          event.stopPropagation();
          onToggleHidden(node);
        }}
        onPointerDown={(event) => event.stopPropagation()}
        onDoubleClick={(event) => event.stopPropagation()}
      >
        {node.hidden ? eyeClosedIcon : eyeIcon}
      </button>
    </div>
  );
};

export const LayersPanel = () => {
  const app = useApp();
  const appState = useExcalidrawAppState();
  const elements = useExcalidrawElements();
  const actionManager = useExcalidrawActionManager();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const lastClickedRef = useRef<string | null>(null);

  const tree = useMemo(
    () =>
      buildLayerTree(elements, (groupId) => getGroupName(elements, groupId)),
    [elements],
  );
  const visibleRows = useMemo(
    () => flattenVisibleNodes(tree, expanded),
    [tree, expanded],
  );

  const toggleExpand = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const setSelection = useCallback(
    (ids: string[], mode: "replace" | "toggle") => {
      app.setState((prev) => {
        const selectedElementIds: Record<string, true> =
          mode === "toggle"
            ? ({ ...prev.selectedElementIds } as Record<string, true>)
            : {};
        for (const id of ids) {
          if (mode === "toggle" && selectedElementIds[id]) {
            delete selectedElementIds[id];
          } else {
            selectedElementIds[id] = true;
          }
        }
        return selectGroupsForSelectedElements(
          { editingGroupId: null, selectedElementIds },
          app.scene.getNonDeletedElements(),
          prev,
          app,
        );
      });
    },
    [app],
  );

  const onRowClick = useCallback(
    (node: LayerNode, event: React.MouseEvent) => {
      const ids = selectionElementIds(node);
      if (event.shiftKey && lastClickedRef.current) {
        const rowIds = visibleRows.map((row) => row.id);
        const from = rowIds.indexOf(lastClickedRef.current);
        const to = rowIds.indexOf(node.id);
        if (from !== -1 && to !== -1) {
          const [start, end] = from < to ? [from, to] : [to, from];
          const rangeIds = visibleRows
            .slice(start, end + 1)
            .flatMap(selectionElementIds);
          setSelection(rangeIds, "replace");
          return;
        }
      }
      lastClickedRef.current = node.id;
      setSelection(ids, event.ctrlKey || event.metaKey ? "toggle" : "replace");
    },
    [visibleRows, setSelection],
  );

  const isNodeSelected = useCallback(
    (node: LayerNode): boolean => {
      const ids = selectionElementIds(node);
      return (
        ids.length > 0 && ids.every((id) => appState.selectedElementIds[id])
      );
    },
    [appState.selectedElementIds],
  );

  const hasSelectedDescendants = useCallback(
    (node: LayerNode): boolean => {
      if (node.children.length === 0) {
        return false;
      }
      return node.children.some(
        (child) => isNodeSelected(child) || hasSelectedDescendants(child),
      );
    },
    [isNodeSelected],
  );

  // reveal canvas selections: expand ancestors of selected rows so the
  // highlighted row is visible (Figma behavior). The topmost fully-selected
  // node is the row we highlight, so ancestors above it expand — the node
  // itself stays collapsed.
  const selectionKey = useMemo(
    () => Object.keys(appState.selectedElementIds).sort().join(","),
    [appState.selectedElementIds],
  );
  const lastRevealedRef = useRef("");
  useEffect(() => {
    if (lastRevealedRef.current === selectionKey) {
      return;
    }
    lastRevealedRef.current = selectionKey;
    if (!selectionKey) {
      return;
    }
    const toExpand: string[] = [];
    const walk = (nodes: LayerNode[]): boolean => {
      let hit = false;
      for (const node of nodes) {
        if (isNodeSelected(node)) {
          hit = true;
          continue;
        }
        if (walk(node.children)) {
          toExpand.push(node.id);
          hit = true;
        }
      }
      return hit;
    };
    walk(tree);
    if (toExpand.length) {
      setExpanded((prev) => {
        if (toExpand.every((id) => prev.has(id))) {
          return prev;
        }
        const next = new Set(prev);
        toExpand.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [selectionKey, tree, isNodeSelected]);

  const onToggleHidden = useCallback(
    (node: LayerNode) => {
      actionManager.executeAction(actionToggleElementHidden, "ui", {
        ids: collectElementIds(node),
      });
    },
    [actionManager],
  );

  const onToggleLock = useCallback(
    (node: LayerNode) => {
      const ids = new Set(collectElementIds(node));
      const targets = app.scene
        .getNonDeletedElements()
        .filter((element) => ids.has(element.id));
      const nextLocked = targets.some((element) => !element.locked);
      app.updateScene({
        elements: app.scene
          .getElementsIncludingDeleted()
          .map((element) =>
            ids.has(element.id)
              ? newElementWith(element, { locked: nextLocked })
              : element,
          ),
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      });
    },
    [app],
  );

  const onRenameCommit = useCallback(
    (node: LayerNode, value: string) => {
      setRenamingId(null);
      actionManager.executeAction(
        actionRenameElement,
        "ui",
        node.kind === "group"
          ? { groupId: node.id, name: value }
          : { elementId: node.id, name: value },
      );
    },
    [actionManager],
  );

  const onRenameCancel = useCallback(() => {
    setRenamingId(null);
  }, []);

  const onRowDoubleClick = useCallback((node: LayerNode) => {
    setRenamingId(node.id);
  }, []);

  // ---- drag & drop ----
  const [drag, setDrag] = useState<{
    ids: string[];
    target: DropTarget | null;
  } | null>(null);
  // mirror of `drag` readable synchronously — a fast drag can deliver
  // pointermove + pointerup before React re-renders, leaving the state
  // closure stale in the pointerup handler
  const dragRef = useRef<typeof drag>(null);
  const setDragBoth = useCallback(
    (value: typeof drag) => {
      dragRef.current = value;
      setDrag(value);
    },
    [setDrag],
  );
  const pressRef = useRef<{
    startX: number;
    startY: number;
    nodeId: string;
  } | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const onRowPointerDown = useCallback(
    (node: LayerNode, event: React.PointerEvent) => {
      if (event.button !== 0 || renamingId) {
        return;
      }
      pressRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        nodeId: node.id,
      };
    },
    [renamingId],
  );

  const hitTarget = useCallback(
    (event: React.PointerEvent): DropTarget | null => {
      // elementFromPoint, not event.target: once the list has pointer
      // capture, move events are retargeted to the list itself
      const rowEl = document
        .elementFromPoint(event.clientX, event.clientY)
        ?.closest?.(".layers-panel__row") as HTMLElement | null;
      if (!rowEl) {
        return null;
      }
      const nodeId = rowEl.getAttribute("data-node-id")!;
      const kind = rowEl.getAttribute("data-kind");
      const rect = rowEl.getBoundingClientRect();
      const ratio = (event.clientY - rect.top) / rect.height;
      const isContainer = kind === "group" || kind === "frame";
      const position: DropTarget["position"] =
        ratio < 0.25
          ? "above"
          : ratio > 0.75
          ? "below"
          : isContainer
          ? "inside"
          : ratio < 0.5
          ? "above"
          : "below";
      return { nodeId, position };
    },
    [],
  );

  const onListPointerMove = useCallback(
    (event: React.PointerEvent) => {
      const press = pressRef.current;
      if (!press) {
        return;
      }
      if (!dragRef.current) {
        const dx = event.clientX - press.startX;
        const dy = event.clientY - press.startY;
        if (Math.hypot(dx, dy) < 4) {
          return;
        }
        // dragged set = selection if the pressed row is part of it,
        // otherwise just the pressed row
        const pressedNode = visibleRows.find((row) => row.id === press.nodeId);
        const pressedSelected = pressedNode && isNodeSelected(pressedNode);
        const ids = pressedSelected
          ? visibleRows.filter(isNodeSelected).map((row) => row.id)
          : [press.nodeId];
        listRef.current?.setPointerCapture?.(event.pointerId);
        setDragBoth({ ids, target: hitTarget(event) });
        return;
      }
      setDragBoth({ ...dragRef.current, target: hitTarget(event) });
    },
    [visibleRows, isNodeSelected, hitTarget, setDragBoth],
  );

  const onListPointerUp = useCallback(() => {
    const press = pressRef.current;
    pressRef.current = null;
    if (!dragRef.current) {
      return;
    }
    const { ids, target } = dragRef.current;
    setDragBoth(null);
    if (!press || !target || (ids.length === 1 && ids[0] === target.nodeId)) {
      return;
    }
    const next = computeDropResult(
      app.scene.getElementsIncludingDeleted(),
      tree,
      ids,
      target,
    );
    if (next) {
      app.updateScene({
        elements: next,
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      });
    }
  }, [app, tree, setDragBoth]);

  const onListPointerLeave = useCallback(() => {
    if (!dragRef.current) {
      pressRef.current = null;
    }
  }, []);

  // ---- context menu ----
  const [menu, setMenu] = useState<{
    x: number;
    y: number;
    node: LayerNode;
  } | null>(null);

  const onRowContextMenu = useCallback(
    (node: LayerNode, event: React.MouseEvent) => {
      event.preventDefault();
      // right-click selects the row if not already in selection (Figma)
      if (!isNodeSelected(node)) {
        setSelection(selectionElementIds(node), "replace");
      }
      setMenu({ x: event.clientX, y: event.clientY, node });
    },
    [isNodeSelected, setSelection],
  );

  useEffect(() => {
    if (!menu) {
      return;
    }
    const close = () => setMenu(null);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        close();
      }
    };
    window.addEventListener("pointerdown", close);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", close);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menu]);

  type MenuItem =
    | "separator"
    | { label: string; action?: any; onClick?: () => void };

  const menuItems: MenuItem[] = menu
    ? [
        { label: "Group", action: actionGroup },
        { label: "Ungroup", action: actionUngroup },
        "separator",
        { label: "Bring forward", action: actionBringForward },
        { label: "Send backward", action: actionSendBackward },
        { label: "Bring to front", action: actionBringToFront },
        { label: "Send to back", action: actionSendToBack },
        "separator",
        { label: "Rename", onClick: () => setRenamingId(menu.node.id) },
        {
          label: menu.node.hidden ? "Show" : "Hide",
          onClick: () => onToggleHidden(menu.node),
        },
        {
          label: menu.node.locked ? "Unlock" : "Lock",
          onClick: () => onToggleLock(menu.node),
        },
        "separator",
        { label: "Delete", action: actionDeleteSelected },
      ]
    : [];

  const renderNodes = (nodes: LayerNode[], depth: number): React.ReactNode =>
    nodes.map((node) => (
      <React.Fragment key={node.id}>
        <LayerRow
          node={node}
          depth={depth}
          expanded={expanded.has(node.id)}
          selected={isNodeSelected(node)}
          hasSelectedChildren={hasSelectedDescendants(node)}
          renaming={renamingId === node.id}
          dropIndicator={
            drag?.target?.nodeId === node.id ? drag.target.position : null
          }
          onToggleExpand={toggleExpand}
          onRowClick={onRowClick}
          onRowDoubleClick={onRowDoubleClick}
          onRowContextMenu={onRowContextMenu}
          onRowPointerDown={onRowPointerDown}
          onRenameCommit={onRenameCommit}
          onRenameCancel={onRenameCancel}
          onToggleHidden={onToggleHidden}
          onToggleLock={onToggleLock}
        />
        {node.children.length > 0 &&
          expanded.has(node.id) &&
          renderNodes(node.children, depth + 1)}
      </React.Fragment>
    ));

  return (
    <div className={`layers-panel${drag ? " layers-panel--dragging" : ""}`}>
      <div
        ref={listRef}
        className="layers-panel__list"
        onPointerMove={onListPointerMove}
        onPointerUp={onListPointerUp}
        // a press that leaves the list before crossing the drag threshold
        // never took pointer capture, so its pointerup lands elsewhere and
        // `pressRef` would survive — the next hover over the list would then
        // start a drag with no button held. Abandon the press instead.
        onPointerLeave={onListPointerLeave}
      >
        {renderNodes(tree, 0)}
      </div>
      {menu &&
        createPortal(
          <div
            className="layers-panel__menu"
            style={{
              left: Math.min(menu.x, window.innerWidth - 180),
              top: Math.min(menu.y, window.innerHeight - 320),
            }}
            onPointerDown={(event) => event.stopPropagation()}
          >
            {menuItems.map((item, index) =>
              item === "separator" ? (
                <div className="layers-panel__menu-separator" key={index} />
              ) : (
                <button
                  type="button"
                  className="layers-panel__menu-item"
                  key={item.label}
                  onClick={() => {
                    if (item.action) {
                      actionManager.executeAction(item.action, "contextMenu");
                    } else {
                      item.onClick?.();
                    }
                    setMenu(null);
                  }}
                >
                  {item.label}
                </button>
              ),
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};
